import {useCallback, useEffect, useState} from 'react';

const STORAGE_KEY = 'userToken';

const parseStoredUser = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    console.warn('Failed to parse stored user token:', error);
    return null;
  }
};

const getStoredUser = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return parseStoredUser(raw);
  } catch (error) {
    console.warn('Failed to read stored user token:', error);
    return null;
  }
};

const isGuestUser = (user) => {
  if (!user) return false;
  return !!(user.guest === true || user.lgnUserNm === 'guest' || user.userNm === 'guest' || user.loginId === 'guest');
};

export default function useCurrentUser() {
  const [user, setUser] = useState(() => getStoredUser());

  const refresh = useCallback(() => {
    setUser(getStoredUser());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleAuthContextChanged = (event) => {
      if (event?.detail) {
        setUser(event.detail);
      } else {
        refresh();
      }
    };

    const handleStorage = (event) => {
      if (event?.key === STORAGE_KEY) {
        refresh();
      }
    };

    const handleTokenRefreshed = (event) => {
      if (event?.detail) {
        setUser(event.detail);
      }
    };

    refresh();

    window.addEventListener('auth-context-changed', handleAuthContextChanged);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('token-refreshed', handleTokenRefreshed);

    return () => {
      window.removeEventListener('auth-context-changed', handleAuthContextChanged);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('token-refreshed', handleTokenRefreshed);
    };
  }, [refresh]);

  const isAuthenticated = !!(user && !isGuestUser(user));

  return { user, isAuthenticated, refresh };
}
